import { initialState } from '..'
import {
  FETCH_STATISTICS_FAILED,
  SET,
  STORE_FETCHED_STATISTICS,
  REFETCHED,
  JobsActions,
} from '../../actions/jobs/jobs'
import type { SiteAlias } from '../../../schema/webshop-package'
import type { ApplyVia, DownStreamSalaryType, FunctionGroupType, NumberRange } from '../draft/draft'
import type { JobPostingProduct, JobPostingUpgradeEntitlement } from '../entitlement/entitlement'
import type { PostalAddress } from "../../../schema/user"
import type { JobStatus as ImportedJobStatus } from '../../../service/job/fetch-jobs/fetchJobs'
import { RemoteWork } from '../../../service/util/master-data/masterdata'
import { RejectionReason as RejectionReasonType } from '../../../schema/mappers/job'

export type RejectionReason = RejectionReasonType

export type JobStatus = ImportedJobStatus

export type SiteAbbreviations = 'iol' | 'nvb' | 'itb'

export type Filter = {
  name: JobStatus,
  count: number,
}

export type Filters = Array<Filter>

export type UpgradeRejectionReason = string // 'InsufficientCredits' | 'Other'

export type Location = {
  country: string,
  postalCode: string,
}

export type Logo = {
  key?: string,
}

export type CustomField = {
  index: number,
  title: string,
  content: string,
}
export type CustomFields = Array<CustomField>

export type Video = {
  url: string,
}

export type ContactInformation = {
  contact: {
    name: {
      firstName: string,
      lastName: string,
    },
    emailAddress: string,
    phoneNumber?: string,
  },
  address?: PostalAddress,
  website?: string,
}

// this type shapes the job data to/from the endpoints
export type JobContent = {
  content: {
    title: string,
    description: string,
    functionGroup: FunctionGroupType
    jobCategories: string[],
    industryCategories: string[],
  },
  criteria: {
    educationLevels: string[],
    careerLevels: string[],
  },
  contract: {
    contractTypes: string[],
    workingHours: NumberRange,
    salary?: DownStreamSalaryType,
    remoteWork?: RemoteWork | '',
  },
  publicationPeriod: {
    start: string,
    end: string,
  },
  location: Location,
  applyVia: ApplyVia,
  company?: {
    name: string,
    companyType: string,
    contact?: {
      name: {
        firstName: string,
        lastName: string,
      },
      phoneNumber: string,
      emailAddress: string,
    },
    address?: {},
    description?: string,
    huisstijl?: string | number,
    status?: 'Unverified',
    website?: string,
  },
  logo?: Logo,
  contactInformation?: ContactInformation,
  customFields: CustomFields,
  video?: Video,
}

export type RequestedUpgradeStatus = {
  name: string,
  features: Array<string>,
  requiredFeatures: Array<string>,
  requiredSites: Array<SiteAlias>,
  status: {
    value: string,
    error?: UpgradeRejectionReason,
  },
}

export type UpgradeHistory = Array<RequestedUpgradeStatus>

export type UpgradedJobPostingProduct = JobPostingProduct & {
  upgrades?: UpgradeHistory,
}

export type Job = {
  id: string,
  title: string,
  status: {
    value: JobStatus,
    error?: RejectionReason,
  },
  product: UpgradedJobPostingProduct | JobPostingProduct,
  availableUpgrades?: JobPostingUpgradeEntitlement,
  createdAt: string,
  publishedOnUrl: Partial<Record<SiteAbbreviations, string>>,
  content?: JobContent,
  startDate?: string,
  endDate?: string,
  workLocation?: string,
  salaryRange?: any,
}

export type Jobs = Array<Job>

export type JobStatistics = {
  apply: number,
  applyClick: number,
  detailView: number,
  searchResult: number,
  topbaanResult: number,
  jobId: string,
  detailViewBooster: number,
  applyClickBooster: number,
  boosterActivatedAt: Date,
}

export type JobsStatistics = {
  [jobId: string]: JobStatistics,
}

export type JobsStoreType = {
  jobs: Jobs,
  pagination: {
    filterAmounts: Filters,
  },
  statistics: JobsStatistics,
  statisticsFailed: boolean,
}

const reducer = (state: JobsStoreType, { type, payload }: JobsActions): JobsStoreType => {
  if (!state) {
    return initialState.jobs
  }

  if (type === SET) {
    return {
      ...state,
      jobs: payload.jobs,
      pagination: {
        ...state.pagination,
        filterAmounts: payload.filterAmounts,
      },
    }
  }

  if (type === REFETCHED) {
    const currentJobs = [...state.jobs]
    const indexOfRefetchedJob = currentJobs.findIndex(job => job.id === payload.id)
    if (indexOfRefetchedJob !== -1) {
      currentJobs[indexOfRefetchedJob] = payload
      return {
        ...state,
        jobs: currentJobs,
      }
    }

    return {
      ...state,
    }
  }

  if (type === STORE_FETCHED_STATISTICS) {
    return {
      ...state,
      statistics: payload,
      statisticsFailed: false,
    }
  }

  if (type === FETCH_STATISTICS_FAILED) {
    return {
      ...state,
      statisticsFailed: true,
    }
  }

  return state
}

export default reducer
